import { apiClient, ApiResponse, RequestConfig } from './apiClient';
import {
  ErrorAnalyticsResponse,
  ErrorStatistics,
  ErrorTrend,
  RecentError,
  TopError,
  ErrorReportRequest,
  ErrorReportResponse,
  ErrorFeedbackRequest,
  ErrorFeedbackResponse,
  ErrorHealthCheck,
  ErrorExportOptions,
  ErrorExportResponse
} from './types/api';

/**
 * Error Analytics Service
 * 
 * Provides comprehensive error analytics functionality including:
 * - Error statistics retrieval with caching
 * - Error trend analysis
 * - Error reporting and feedback
 * - Export functionality for error reports
 * - Health monitoring for error tracking system
 * 
 * Features:
 * - 5-minute TTL caching for performance optimization
 * - Proper error handling and loading states
 * - Fallback mechanisms for API failures
 * - Integration with existing apiClient patterns
 */
export class ErrorAnalyticsService {
  private static instance: ErrorAnalyticsService;
  private readonly baseEndpoint = '/errors';
  private readonly defaultCacheTTL = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): ErrorAnalyticsService {
    if (!ErrorAnalyticsService.instance) {
      ErrorAnalyticsService.instance = new ErrorAnalyticsService();
    }
    return ErrorAnalyticsService.instance;
  }

  /**
   * Get error statistics for a specific timeframe
   * Uses caching with 5-minute TTL for performance optimization
   */
  async getErrorStatistics(
    timeframe: '1h' | '6h' | '24h' | '7d' = '24h',
    options?: {
      skipCache?: boolean;
      cacheTTL?: number;
    }
  ): Promise<ApiResponse<ErrorAnalyticsResponse>> {
    try {
      const config: RequestConfig = {
        cache: !options?.skipCache,
        cacheTTL: options?.cacheTTL || this.defaultCacheTTL,
        performanceTracking: true,
        priority: 'normal'
      };

      console.log('🔍 ErrorAnalyticsService: Fetching error statistics', {
        timeframe,
        cacheEnabled: config.cache,
        cacheTTL: config.cacheTTL,
        timestamp: new Date().toISOString()
      });

      const response = await apiClient.get<ErrorAnalyticsResponse>(
        `${this.baseEndpoint}/stats?timeframe=${timeframe}`,
        config
      );

      // Validate response structure
      if (response.success && response.data) {
        this.validateErrorStatistics(response.data);
        console.log('✅ ErrorAnalyticsService: Error statistics retrieved successfully', {
          timeframe,
          totalErrors: response.data.stats.totalErrors,
          generatedAt: response.data.generatedAt
        });
      }

      return response;
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to fetch error statistics', {
        timeframe,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });

      // Return fallback data structure for graceful degradation
      return this.createFallbackResponse(timeframe);
    }
  }

  /**
   * Get error trends over time for visualization
   * Processes error statistics to create trend data
   */
  async getErrorTrends(
    timeframe: '1h' | '6h' | '24h' | '7d' = '24h',
    options?: {
      groupBy?: 'hour' | 'day';
      filterByLevel?: ('page' | 'component' | 'widget')[];
    }
  ): Promise<ApiResponse<ErrorTrend[]>> {
    try {
      const statsResponse = await this.getErrorStatistics(timeframe);
      
      if (!statsResponse.success || !statsResponse.data) {
        throw new Error('Failed to fetch error statistics for trends');
      }

      // Generate trend data based on timeframe
      const trends = this.generateTrendData(statsResponse.data, timeframe, options);

      return {
        success: true,
        data: trends,
        message: 'Error trends calculated successfully'
      };
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to calculate error trends', {
        timeframe,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to calculate error trends',
        data: []
      };
    }
  }

  /**
   * Get recent errors with pagination support
   */
  async getRecentErrors(
    limit: number = 10,
    offset: number = 0
  ): Promise<ApiResponse<RecentError[]>> {
    try {
      const statsResponse = await this.getErrorStatistics('24h');
      
      if (!statsResponse.success || !statsResponse.data) {
        throw new Error('Failed to fetch recent errors');
      }

      const recentErrors = statsResponse.data.stats.recentErrors
        .slice(offset, offset + limit);

      return {
        success: true,
        data: recentErrors,
        message: 'Recent errors retrieved successfully'
      };
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to fetch recent errors', {
        limit,
        offset,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to fetch recent errors',
        data: []
      };
    }
  }

  /**
   * Get top errors by frequency
   */
  async getTopErrors(limit: number = 5): Promise<ApiResponse<TopError[]>> {
    try {
      const statsResponse = await this.getErrorStatistics('7d'); // Use 7-day window for better insights
      
      if (!statsResponse.success || !statsResponse.data) {
        throw new Error('Failed to fetch top errors');
      }

      const topErrors = statsResponse.data.stats.topErrors.slice(0, limit);

      return {
        success: true,
        data: topErrors,
        message: 'Top errors retrieved successfully'
      };
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to fetch top errors', {
        limit,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to fetch top errors',
        data: []
      };
    }
  }

  /**
   * Report a frontend error to the backend
   */
  async reportError(errorData: ErrorReportRequest): Promise<ApiResponse<ErrorReportResponse>> {
    try {
      console.log('🔍 ErrorAnalyticsService: Reporting error', {
        errorId: errorData.errorId,
        level: errorData.level,
        message: errorData.message.substring(0, 100),
        url: errorData.url
      });

      const response = await apiClient.post<ErrorReportResponse>(
        `${this.baseEndpoint}/report`,
        errorData,
        {
          skipErrorHandling: false, // We want to handle errors for error reporting
          retries: 2, // Retry failed error reports
          priority: 'high' // Error reporting is high priority
        }
      );

      if (response.success) {
        console.log('✅ ErrorAnalyticsService: Error reported successfully', {
          errorId: errorData.errorId,
          sentryEventId: response.data?.sentryEventId
        });

        // Invalidate cache after reporting new error
        this.invalidateCache();
      }

      return response;
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to report error', {
        errorId: errorData.errorId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to report error'
      };
    }
  }

  /**
   * Submit feedback for an error
   */
  async submitErrorFeedback(feedbackData: ErrorFeedbackRequest): Promise<ApiResponse<ErrorFeedbackResponse>> {
    try {
      console.log('🔍 ErrorAnalyticsService: Submitting error feedback', {
        errorId: feedbackData.errorId,
        feedbackLength: feedbackData.feedback.length,
        hasReproductionSteps: !!feedbackData.reproductionSteps
      });

      const response = await apiClient.post<ErrorFeedbackResponse>(
        `${this.baseEndpoint}/feedback`,
        feedbackData,
        {
          priority: 'normal',
          retries: 1
        }
      );

      if (response.success) {
        console.log('✅ ErrorAnalyticsService: Error feedback submitted successfully', {
          errorId: feedbackData.errorId,
          databaseId: response.data?.databaseId
        });
      }

      return response;
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to submit error feedback', {
        errorId: feedbackData.errorId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to submit error feedback'
      };
    }
  }

  /**
   * Export error reports in various formats
   */
  async exportErrorReports(options: ErrorExportOptions): Promise<ApiResponse<ErrorExportResponse>> {
    try {
      console.log('🔍 ErrorAnalyticsService: Exporting error reports', {
        timeframe: options.timeframe,
        format: options.format,
        includeStackTraces: options.includeStackTraces,
        filterByLevel: options.filterByLevel
      });

      // For now, generate a client-side export since backend doesn't have this endpoint yet
      const statsResponse = await this.getErrorStatistics(options.timeframe);
      
      if (!statsResponse.success || !statsResponse.data) {
        throw new Error('Failed to fetch error data for export');
      }

      const exportData = this.generateExportData(statsResponse.data, options);
      const downloadUrl = this.createDownloadUrl(exportData, options.format);

      const exportResponse: ErrorExportResponse = {
        exportId: `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        downloadUrl,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        format: options.format,
        recordCount: statsResponse.data.stats.recentErrors.length
      };

      console.log('✅ ErrorAnalyticsService: Error reports exported successfully', {
        exportId: exportResponse.exportId,
        format: options.format,
        recordCount: exportResponse.recordCount
      });

      return {
        success: true,
        data: exportResponse,
        message: 'Error reports exported successfully'
      };
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to export error reports', {
        options,
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      return {
        success: false,
        error: 'Failed to export error reports'
      };
    }
  }

  /**
   * Check health status of error tracking system
   */
  async getHealthStatus(): Promise<ApiResponse<ErrorHealthCheck>> {
    try {
      const response = await apiClient.get<ErrorHealthCheck>(
        `${this.baseEndpoint}/health`,
        {
          cache: false, // Always get fresh health data
          skipErrorHandling: true // Handle errors manually for health checks
        }
      );

      return response;
    } catch (error) {
      console.error('❌ ErrorAnalyticsService: Failed to get health status', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      // Return degraded status if health check fails
      return {
        success: true,
        data: {
          errorTracking: {
            sentry: {
              initialized: false,
              status: 'disabled'
            },
            endpoints: {
              report: 'down',
              stats: 'down',
              feedback: 'down'
            }
          },
          timestamp: new Date().toISOString()
        },
        message: 'Error tracking system health check failed'
      };
    }
  }

  /**
   * Refresh error statistics by invalidating cache
   */
  async refreshStatistics(timeframe: '1h' | '6h' | '24h' | '7d' = '24h'): Promise<ApiResponse<ErrorAnalyticsResponse>> {
    console.log('🔄 ErrorAnalyticsService: Refreshing error statistics', { timeframe });
    
    // Invalidate cache first
    this.invalidateCache();
    
    // Fetch fresh data
    return this.getErrorStatistics(timeframe, { skipCache: true });
  }

  /**
   * Private helper methods
   */

  private validateErrorStatistics(data: ErrorAnalyticsResponse): void {
    if (!data.stats || typeof data.stats.totalErrors !== 'number') {
      throw new Error('Invalid error statistics response structure');
    }

    if (!data.stats.errorsByLevel || !data.stats.errorsByTimeframe) {
      throw new Error('Missing required error statistics fields');
    }
  }

  private createFallbackResponse(timeframe: string): ApiResponse<ErrorAnalyticsResponse> {
    return {
      success: true,
      data: {
        timeframe,
        stats: {
          totalErrors: 0,
          errorsByLevel: { page: 0, component: 0, widget: 0 },
          errorsByTimeframe: { '1h': 0, '6h': 0, '24h': 0, '7d': 0 },
          topErrors: [],
          recentErrors: []
        },
        generatedAt: new Date().toISOString()
      },
      message: 'Fallback error statistics (API unavailable)'
    };
  }

  private generateTrendData(
    data: ErrorAnalyticsResponse,
    timeframe: string,
    options?: { groupBy?: 'hour' | 'day'; filterByLevel?: ('page' | 'component' | 'widget')[] }
  ): ErrorTrend[] {
    // Generate trend data based on timeframe and grouping
    const trends: ErrorTrend[] = [];
    const now = new Date();
    const groupBy = options?.groupBy || (timeframe === '1h' || timeframe === '6h' ? 'hour' : 'day');
    
    // Calculate number of data points based on timeframe
    const dataPoints = timeframe === '1h' ? 12 : timeframe === '6h' ? 24 : timeframe === '24h' ? 24 : 7;
    const intervalMs = timeframe === '1h' ? 5 * 60 * 1000 : // 5 minutes
                      timeframe === '6h' ? 15 * 60 * 1000 : // 15 minutes
                      timeframe === '24h' ? 60 * 60 * 1000 : // 1 hour
                      24 * 60 * 60 * 1000; // 1 day

    for (let i = dataPoints - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * intervalMs);
      
      // Simulate trend data based on actual error counts
      // In a real implementation, this would come from time-series data
      const errorCount = Math.floor(data.stats.totalErrors / dataPoints * (0.5 + Math.random()));
      
      trends.push({
        date: date.toISOString(),
        errorCount
      });
    }

    return trends;
  }

  private generateExportData(data: ErrorAnalyticsResponse, options: ErrorExportOptions): string {
    const { format, includeStackTraces, includeUserInfo, filterByLevel } = options;
    
    let filteredErrors = data.stats.recentErrors;
    
    // Apply level filter if specified
    if (filterByLevel && filterByLevel.length > 0) {
      filteredErrors = filteredErrors.filter(error => filterByLevel.includes(error.level));
    }

    switch (format) {
      case 'csv':
        return this.generateCSV(filteredErrors, includeStackTraces, includeUserInfo);
      case 'json':
        return JSON.stringify({
          exportMetadata: {
            timeframe: data.timeframe,
            generatedAt: data.generatedAt,
            totalErrors: data.stats.totalErrors,
            exportedErrors: filteredErrors.length
          },
          errors: filteredErrors
        }, null, 2);
      case 'pdf':
        // For PDF, return JSON that can be processed by a PDF generator
        return JSON.stringify({
          type: 'pdf',
          title: 'Error Analytics Report',
          data: filteredErrors,
          metadata: data.stats
        });
      default:
        return JSON.stringify(filteredErrors, null, 2);
    }
  }

  private generateCSV(errors: RecentError[], includeStackTraces?: boolean, includeUserInfo?: boolean): string {
    const headers = ['Error ID', 'Message', 'Level', 'Timestamp', 'URL'];
    const rows = errors.map(error => [
      error.errorId,
      `"${error.message.replace(/"/g, '""')}"`, // Escape quotes in CSV
      error.level,
      error.timestamp,
      error.url
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  private createDownloadUrl(data: string, format: string): string {
    const blob = new Blob([data], { 
      type: format === 'csv' ? 'text/csv' : 'application/json' 
    });
    return URL.createObjectURL(blob);
  }

  private invalidateCache(): void {
    // Clear cache for error statistics endpoints
    apiClient.clearCache();
    console.log('🔄 ErrorAnalyticsService: Cache invalidated');
  }
}

// Export singleton instance
export const errorAnalyticsService = ErrorAnalyticsService.getInstance();
export default errorAnalyticsService;